import { useCallback } from 'react';
import { useActiveEditTarget } from '~/hooks/target/useActiveEditTarget';
import {
    ROI_POINT_TYPES,
    RoiIdentificationCodeSequence,
    RoiInterpretedType,
} from '~/domain/dicom/dicomstructureset';
import {
    domainEventBus,
    RoiContourThicknessChangeRequestedEvent,
    RoiCopyRequestedEvent,
    RoiElectronDensityOverrideChangeRequestedEvent,
    RoiMassDensityOverrideChangeRequestedEvent,
    RoiDescriptionChangeRequestedEvent,
    RoiInterpretedTypeChangeRequestedEvent,
    RoiLockChangeRequestedEvent,
    RoiPointCoordinatesChangeRequestedEvent,
    RoiSdfCopyOverRequestedEvent,
    RoiSelectionRequestedEvent,
    RoiVisibilityChangeRequestedEvent,
} from '~/domain/events';
import type { RoiId } from '~/domain/changes/ids';
import { RoiReadModel } from '~/domain/readmodels/StructureSetReadModel';
import type { Grid } from '~/engine/data/common/Grid';
import { localization } from '~/i18n';
import { enqueueSnackbar } from '~/utils/notifications';

/**
 * How far inside the image edge a point held there sits, mm: below the 0.1 mm
 * the coordinates are shown to, above the float32 rounding of a position.
 */
export const POINT_EDGE_INSET_MM = 0.01;

type PointCoordinatesMm = { x: number; y: number; z: number };

/**
 * `coordinates` (mm), or, past the edge of the image `grid` describes, the
 * nearest position in it, with the user told. Unchanged with no image.
 */
export function holdPointInsideImage(
    grid: Grid | undefined,
    coordinates: PointCoordinatesMm
): PointCoordinatesMm {
    const { x, y, z } = coordinates;
    if (!grid || grid.includesPoint([x, y, z])) {
        return coordinates;
    }
    enqueueSnackbar(localization.t('poi.coordinates.heldInsideImage'), {
        variant: 'warning',
    });
    const [heldX, heldY, heldZ] = grid.clampPoint(
        [x, y, z],
        POINT_EDGE_INSET_MM
    );
    return { x: heldX, y: heldY, z: heldZ };
}

export const formatIdentificationCode = (
    code: RoiIdentificationCodeSequence | undefined
) => {
    return code
        ? `${code.codeValue} ${code.codingSchemeDesignator} ${code.codeMeaning}`
        : '';
};

/**
 * Hook for managing ROI operations via domain events.
 *
 * Provides functions to modify ROI properties by publishing domain events
 * to the event bus. All operations are handled asynchronously through the event-driven architecture.
 * The actual state updates are performed by event handlers, maintaining separation of concerns.
 *
 * @param roiInstance The ROI instance to operate on
 * @returns ROI domain event publishing functions.
 */
const useRoi = (roiInstance: RoiReadModel) => {
    // Resolved through the edit target: the grid Environment that shows the
    // active set is the one that receives the strokes, so its volume and
    // emptiness are the ones the clinician sees.
    const { environment: env } = useActiveEditTarget();

    const getRoiVolumeCm3 = useCallback(() => {
        return env?.getRoiVolumeCm3(roiInstance.roiId) ?? Number.NaN;
    }, [env, roiInstance.roiId]);

    const isRoiEmpty = useCallback(() => {
        return env?.isRoiEmpty(roiInstance.roiId) ?? true;
    }, [env, roiInstance.roiId]);

    const getRoiEquivalentSphereDiameterCm = useCallback(() => {
        return 2 * Math.cbrt((3 * getRoiVolumeCm3()) / (4 * Math.PI));
    }, [getRoiVolumeCm3]);

    const setVisible = useCallback(
        async (visible: boolean) => {
            try {
                await domainEventBus.publish(
                    new RoiVisibilityChangeRequestedEvent(
                        roiInstance.roiId,
                        visible,
                        'user'
                    )
                );
            } catch (error) {
                console.error(`Error updating ROI visibility: ${error}`);
            }
        },
        [roiInstance.roiId]
    );

    const setLocked = useCallback(
        async (locked: boolean) => {
            try {
                await domainEventBus.publish(
                    new RoiLockChangeRequestedEvent(
                        roiInstance.roiId,
                        locked,
                        'user'
                    )
                );
            } catch {
                console.error('Error updating ROI lock');
            }
        },
        [roiInstance.roiId]
    );

    const setContourThickness = useCallback(
        async (newThickness: number) => {
            try {
                await domainEventBus.publish(
                    new RoiContourThicknessChangeRequestedEvent(
                        roiInstance.roiId,
                        newThickness,
                        'user'
                    )
                );
            } catch (error) {
                console.error(`Error updating ROI contour thickness: ${error}`);
            }
        },
        [roiInstance.roiId]
    );

    const setPointCoordinates = useCallback(
        async (coordinates: PointCoordinatesMm) => {
            try {
                await domainEventBus.publish(
                    new RoiPointCoordinatesChangeRequestedEvent(
                        roiInstance.roiId,
                        holdPointInsideImage(env?.image?.grid, coordinates),
                        'user'
                    )
                );
            } catch (error) {
                console.error(`Error updating ROI point coordinates: ${error}`);
            }
        },
        [env, roiInstance.roiId]
    );

    const setInterpretedType = useCallback(
        async (newType: RoiInterpretedType) => {
            try {
                await domainEventBus.publish(
                    new RoiInterpretedTypeChangeRequestedEvent(
                        roiInstance.roiId,
                        newType,
                        'user'
                    )
                );

                // When switching from a volumetric type to a point type,
                // auto-place the point at the current crosshair position so
                // the user never has to manually set an initial coordinate.
                const wasPointType = ROI_POINT_TYPES.includes(
                    roiInstance.interpretedType
                );
                const isNowPointType = ROI_POINT_TYPES.includes(newType);
                if (!wasPointType && isNowPointType) {
                    const coords = env?.planeIntersectionPoint.coordinates;
                    if (coords) {
                        await setPointCoordinates({
                            x: coords[0],
                            y: coords[1],
                            z: coords[2],
                        });
                    }
                }
            } catch (error) {
                console.error(`Error updating ROI interpreted type: ${error}`);
            }
        },
        [
            roiInstance.roiId,
            roiInstance.interpretedType,
            env,
            setPointCoordinates,
        ]
    );

    const setElectronDensityOverride = useCallback(
        async (newDensity: number | undefined) => {
            try {
                await domainEventBus.publish(
                    new RoiElectronDensityOverrideChangeRequestedEvent(
                        roiInstance.roiId,
                        newDensity,
                        'user'
                    )
                );
            } catch (error) {
                console.error(
                    `Error updating ROI electron density override: ${error}`
                );
            }
        },
        [roiInstance.roiId]
    );

    const setMassDensityOverride = useCallback(
        async (newDensity: number | undefined) => {
            try {
                await domainEventBus.publish(
                    new RoiMassDensityOverrideChangeRequestedEvent(
                        roiInstance.roiId,
                        newDensity,
                        'user'
                    )
                );
            } catch (error) {
                console.error(
                    `Error updating ROI mass density override: ${error}`
                );
            }
        },
        [roiInstance.roiId]
    );

    const setRoiDescription = useCallback(
        async (newDescription: string) => {
            try {
                await domainEventBus.publish(
                    new RoiDescriptionChangeRequestedEvent(
                        roiInstance.roiId,
                        newDescription,
                        'user'
                    )
                );
            } catch (error) {
                console.error(`Error updating ROI description: ${error}`);
            }
        },
        [roiInstance.roiId]
    );

    const setRoiSelected = useCallback(async () => {
        try {
            await domainEventBus.publish(
                new RoiSelectionRequestedEvent(roiInstance.roiId, 'user')
            );
        } catch (error) {
            console.error(`Error selecting ROI: ${error}`);
        }
    }, [roiInstance.roiId]);

    const copyRoi = useCallback(
        async (structureSetUid: string) => {
            try {
                await domainEventBus.publish(
                    new RoiCopyRequestedEvent(
                        structureSetUid,
                        roiInstance.roiId,
                        'user'
                    )
                );
            } catch (error) {
                console.error(`Error copying ROI: ${error}`);
            }
        },
        [roiInstance.roiId]
    );

    const copySdfOver = useCallback(
        async (structureSetUid: string, targetRoiId: RoiId) => {
            try {
                await domainEventBus.publish(
                    new RoiSdfCopyOverRequestedEvent(
                        structureSetUid,
                        roiInstance.roiId,
                        targetRoiId,
                        'user'
                    )
                );
            } catch (error) {
                console.error(`Error copying SDF over: ${error}`);
            }
        },
        [roiInstance.roiId]
    );

    return {
        getRoiVolumeCm3,
        isRoiEmpty,
        getRoiEquivalentSphereDiameterCm,
        setVisible,
        setLocked,
        setContourThickness,
        setInterpretedType,
        setElectronDensityOverride,
        setMassDensityOverride,
        setPointCoordinates,
        setRoiDescription,
        setRoiSelected,
        copyRoi,
        copySdfOver,
    };
};

export default useRoi;
